import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { del, patch, post } from "common/helpers/HTTP";

import { useCampaignContext } from "../context/CampaignContext";
import { useCampaignFlowContext } from "../context/CampaignFlowContext";
import useUpdateCampaign from "./useUpdateCampaign";

import type {
  CampaignFlow,
  FlowMessageTemplate,
  FlowVoiceTemplate,
} from "../types";

async function addFlowMessageRequest(
  workspaceId: string,
  campaignId: string,
  flowId: string,
  flowMessage: FlowMessageTemplate | FlowVoiceTemplate,
): Promise<Omit<CampaignFlow, "metrics">> {
  return (
    await post(
      `workspaces/${workspaceId}/campaigns/${campaignId}/flows/${flowId}/messages`,
      {
        ...flowMessage,
        attachments:
          "attachments" in flowMessage
            ? flowMessage.attachments.map(({ id }) => id)
            : undefined,
      },
    )
  ).flow;
}
async function deleteLastFlowMessageRequest(
  workspaceId: string,
  campaignId: string,
  flowId: string,
) {
  await del(
    `workspaces/${workspaceId}/campaigns/${campaignId}/flows/${flowId}/messages`,
  );
}

async function updateFlowMessageRequest(
  workspaceId: string,
  campaignId: string,
  flowId: string,
  index: number,
  updates: FlowMessageTemplate | FlowVoiceTemplate,
): Promise<CampaignFlow> {
  return (
    await patch(
      `workspaces/${workspaceId}/campaigns/${campaignId}/flows/${flowId}/messages/${index}`,
      {
        ...updates,
        attachments:
          "attachments" in updates
            ? updates.attachments.map(({ id }) => id)
            : undefined,
      },
    )
  ).flow;
}

export default function useFlowMessages() {
  const queryClient = useQueryClient();
  const { id: campaignId } = useCampaignContext();
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { campaignsKeys } = getQueryKeys(workspaceId);
  const { flow: campaignFlow } = useCampaignFlowContext();
  const { updateCachedCampaign } = useUpdateCampaign();

  const { mutateAsync: addFlowMessage, isPending: isAddingFlowMessage } =
    useMutation({
      mutationFn: ({
        flowMessage,
      }: {
        flowMessage: FlowMessageTemplate | FlowVoiceTemplate;
      }) =>
        addFlowMessageRequest(
          workspaceId,
          campaignId,
          campaignFlow.id,
          flowMessage,
        ),
      onSuccess: (flow) =>
        updateCachedCampaign(campaignId, (draftCampaign) => {
          const flowIndex = draftCampaign.flows.findIndex(
            (draftFlow) => draftFlow.id === flow.id,
          );

          draftCampaign.flows[flowIndex] = {
            ...draftCampaign.flows[flowIndex],
            flow_message_templates: flow.flow_message_templates,
          };
        }),
    });

  const {
    mutateAsync: deleteLastFlowMessage,
    isPending: isDeletingLastFlowMessage,
  } = useMutation({
    mutationFn: () =>
      deleteLastFlowMessageRequest(workspaceId, campaignId, campaignFlow.id),
    onSuccess: () =>
      updateCachedCampaign(campaignId, (draftCampaign) => {
        const flowIndex = draftCampaign.flows.findIndex(
          (draftFlows) => draftFlows.id === campaignFlow.id,
        );

        draftCampaign.flows[flowIndex].flow_message_templates.pop();
      }),
  });

  const { mutateAsync: updateFlowMessage, isPending: isUpdatingFlowMessage } =
    useMutation({
      mutationFn: ({
        index,
        flowMessage,
      }: {
        index: number;
        flowMessage: FlowMessageTemplate | FlowVoiceTemplate;
      }) =>
        updateFlowMessageRequest(
          workspaceId,
          campaignId,
          campaignFlow.id,
          index,
          flowMessage,
        ),
      onMutate: async ({ index, flowMessage }) => {
        await queryClient.cancelQueries({
          queryKey: campaignsKeys.details(campaignId),
        });

        const previousCampaign = queryClient.getQueryData(
          campaignsKeys.details(campaignId),
        );

        updateCachedCampaign(campaignId, (draftCampaign) => {
          const flowIndex = draftCampaign.flows.findIndex(
            (flow) => flow.id === campaignFlow.id,
          );

          draftCampaign.flows[flowIndex].flow_message_templates[index] =
            flowMessage;
        });

        return { previousCampaign };
      },
      onError: (_, { index }, { previousCampaign }) => {
        queryClient.setQueryData(
          campaignsKeys.details(campaignId),
          previousCampaign,
        );

        toast.error(`Error updating flow message #${index}`);
      },
    });

  function addPlaceholderMessage() {
    updateCachedCampaign(campaignId, (draftCampaign) => {
      const flowIndex = draftCampaign.flows.findIndex(
        (flow) => flow.id === campaignFlow.id,
      );

      draftCampaign.flows[flowIndex].flow_message_templates.push({
        delay: 24, // 24 hours (1 day) as default delay
        edited: false,
        message: "",
        original_id: null,
        type: "MESSAGE_TEMPLATE",
        ai_descriptors: {},
        attachments: [],
        campaigns: [],
        id: "",
        created_at: 0,
        metrics: { sent: 0, replies: 0, accepted: 0 },
      });
    });
  }

  function removePlaceholderMessage() {
    updateCachedCampaign(campaignId, (draftCampaign) => {
      const flowIndex = draftCampaign.flows.findIndex(
        (flow) => flow.id === campaignFlow.id,
      );

      draftCampaign.flows[flowIndex].flow_message_templates.pop();
    });
  }

  return {
    addFlowMessage,
    isAddingFlowMessage,
    deleteLastFlowMessage,
    isDeletingLastFlowMessage,
    updateFlowMessage,
    isUpdatingFlowMessage,
    // Placeholder actions
    addPlaceholderMessage,
    removePlaceholderMessage,
  };
}
